import { SpotlightTourStep } from "components/ProductAnnouncement/SpotlightTour";
import {
  ProductAnnouncementPhase,
  useProductAnnouncement,
} from "components/ProductAnnouncement/useProductAnnouncement";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { ProductAnnouncementKey } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useMemo } from "react";

export const USER_PROFILE_MENU_ITEM_ANCHOR_ID = "top-nav-user-profile-menu-item";
export const LINK_FACILITIES_ANCHOR_ID = "edit-user-link-facilities-button";

type UseFacilitySelfAssignmentTourProps = {
  /** Opens the profile button's menu, where the User Profile item lives. Must be stable. */
  openProfileMenu: () => void;
  /** Opens the signed-in user's profile dialog, where the Link Facilities button lives. Must be stable. */
  openProfile: () => void;
  /** Client-side gate for users who can't (or needn't) pick their own facilities. */
  skip: boolean;
};

export type FacilitySelfAssignmentTourState = {
  phase: ProductAnnouncementPhase;
  /** True until the tour is settled: while it's open, waiting its turn behind another announcement, or still being decided. */
  isPending: boolean;
  steps: SpotlightTourStep[];
  completeTour: () => void;
  dismissTour: () => void;
  viewTourStep: (step: number) => void;
};

/**
 * One-time walkthrough for users who may self-assign facilities: the profile
 * menu's User Profile item, then — opening the profile for them — the Link
 * Facilities button. Each step opens what the user would click, so they learn
 * the route. Fires on arrival; there's no intro modal.
 */
export function useFacilitySelfAssignmentTour({
  openProfileMenu,
  openProfile,
  skip,
}: UseFacilitySelfAssignmentTourProps): FacilitySelfAssignmentTourState {
  const { featureFlags } = useFeatureFlags();
  const tourEnabled = featureFlags[FeatureFlagKey.FacilitySelfAssignmentTour] === true;
  const { phase, shouldShow, shouldShowLoading, completeTour, dismissTour, viewTourStep } =
    useProductAnnouncement(ProductAnnouncementKey.FacilitySelfAssignmentTour_2026_10, {
      skip: skip || !tourEnabled,
      initialSurface: "tour",
      // No "don't show again" checkbox, so closing it any way at all ends it for good.
      tourDismissIsTerminal: true,
    });

  const steps = useMemo<SpotlightTourStep[]>(
    () => [
      {
        anchorId: USER_PROFILE_MENU_ITEM_ANCHOR_ID,
        title: "Welcome back",
        body: "It looks like you haven't logged in in a while. Your facility associations are stored here in User Profile, and we'll help you review them in the next step.",
        placement: "left",
        beforeHighlight: openProfileMenu,
      },
      {
        anchorId: LINK_FACILITIES_ANCHOR_ID,
        title: "Review your facilities",
        body: "These are your current facility associations. Missing a site? Click Link Facilities to add it, then Save. Keeping this list current means you see what you're responsible for.",
        placement: "left",
        beforeHighlight: openProfile,
      },
    ],
    [openProfileMenu, openProfile]
  );

  return {
    phase,
    isPending: phase === "tour" || shouldShow || shouldShowLoading,
    steps,
    completeTour,
    dismissTour,
    viewTourStep,
  };
}
